import { useState } from 'react'
import {
  useAdminUsers,
  useUpdateUserTier,
  useUpdateUserTrust,
  useBlockUser,
  useUnblockUser,
} from '../../hooks/useAdmin'
import type { AdminUser } from '../../api/admin'

const TIERS = ['unverified', 'verified_individual', 'verified_professional']

function BlockControl({ user }: { user: AdminUser }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const blockMutation = useBlockUser()
  const unblockMutation = useUnblockUser()

  if (!user.is_active) {
    return (
      <div className="flex items-center gap-1.5">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-800"
          title={user.blocked_reason ?? undefined}
        >
          Blocked
        </span>
        <button
          type="button"
          disabled={unblockMutation.isPending}
          onClick={() => unblockMutation.mutate(user.id)}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          Unblock
        </button>
      </div>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-red-600 hover:text-red-700 font-medium"
      >
        Block
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        placeholder="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-32 rounded border border-gray-300 text-xs px-1.5 py-1"
      />
      <button
        type="button"
        disabled={blockMutation.isPending}
        onClick={() => blockMutation.mutate({ userId: user.id, reason }, { onSuccess: () => setOpen(false) })}
        className="text-xs text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded px-2 py-1 font-medium"
      >
        Confirm
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-xs text-gray-500 hover:text-gray-700"
      >
        Cancel
      </button>
    </div>
  )
}

function TrustCell({ user }: { user: AdminUser }) {
  const [value, setValue] = useState(String(user.trust_multiplier))
  const mutation = useUpdateUserTrust()
  const dirty = value !== String(user.trust_multiplier)

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-16 rounded border border-gray-300 text-xs px-1.5 py-1"
      />
      {dirty && (
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => {
            const n = Math.min(1, Math.max(0, Number(value)))
            mutation.mutate({ userId: user.id, trustMultiplier: n })
          }}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          Apply
        </button>
      )}
    </div>
  )
}

export default function AdminUsersPage() {
  const [q, setQ] = useState('')
  const [tierFilter, setTierFilter] = useState('')
  const { data, isLoading, isError } = useAdminUsers({
    q: q || undefined,
    credibility_tier: tierFilter || undefined,
  })
  const tierMutation = useUpdateUserTier()

  return (
    <div>
      <h1 className="text-lg font-semibold text-gray-900 mb-4">Users</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          placeholder="Search username or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="rounded-lg border border-gray-300 text-sm px-3 py-1.5 w-64"
        />
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="rounded-lg border border-gray-300 text-sm px-2 py-1.5"
        >
          <option value="">All tiers</option>
          {TIERS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading users…</p>}
      {isError && <p className="text-sm text-red-600">Failed to load users.</p>}

      {data && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500">
                <th className="px-3 py-2">Username</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Credibility tier</th>
                <th className="px-3 py-2">Trust</th>
                <th className="px-3 py-2">Pro. request</th>
                <th className="px-3 py-2">Joined</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-3 py-2 font-medium text-gray-900">{u.username}</td>
                  <td className="px-3 py-2 text-gray-600">{u.email}</td>
                  <td className="px-3 py-2 text-gray-600">{u.role}</td>
                  <td className="px-3 py-2">
                    <select
                      value={u.credibility_tier}
                      disabled={tierMutation.isPending}
                      onChange={(e) => tierMutation.mutate({ userId: u.id, tier: e.target.value })}
                      className="rounded border border-gray-300 text-xs px-1.5 py-1"
                    >
                      {TIERS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <TrustCell user={u} />
                  </td>
                  <td className="px-3 py-2 text-gray-600">{u.professional_request_status}</td>
                  <td className="px-3 py-2 text-gray-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <BlockControl user={u} />
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                    No users match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
